/**
 * Terms - the terms of service
 * ----------------------------
 * This file is the page furniture: the heading, the amber banner, the last
 * updated date, and the choice of which language's document to show. The
 * documents themselves are `legal/TermsEn.jsx` and `legal/TermsIt.jsx`.
 *
 * WHY THE PROSE IS NOT IN THE DICTIONARIES like every other string on the
 * site. A legal document is read, and translated, as a document. Four hundred
 * dictionary keys would make it impossible to check that the two versions say
 * the same thing - which is the only property that actually matters here - and
 * it would put a paragraph of Italian contract law next to a button label.
 * Two files, side by side, can be read against each other.
 *
 * NEITHER VERSION IS MARKED AS AUTHORITATIVE, and that is deliberate rather
 * than an oversight. The two are meant to say the same thing. Which one
 * governs if they ever disagree is a question for a lawyer, not for us:
 * naming English authoritative is the usual move, and it is doubtful against
 * Italian consumers who are also parents consenting for a child. It is
 * question 13 in LEGAL_REVIEW.md. Until that is answered, this page claims
 * nothing either way.
 *
 * !! BEFORE YOU LAUNCH !!
 * Still needs a professional read - then delete the amber banner below.
 *
 * If you change anything material in EITHER document, change the other in the
 * same commit and bump TERMS_VERSION in backend/config/features.js. Do NOT
 * bump CONSENT_VERSION unless what a parent agreed to has actually changed -
 * that locks every under-18 out until their parent answers again.
 */

import { FileText } from 'lucide-react';

import BackLink from '../components/BackLink';
import { useI18n } from '../hooks/useI18n';
import TermsEn from './legal/TermsEn';
import TermsIt from './legal/TermsIt';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'ChoreConnect';
/**
 * The date is written out per language rather than once in English.
 *
 * It used to be a single string, so the Italian page read "Ultimo
 * aggiornamento: 17 August 2026" - an English month in an Italian sentence,
 * on the one page where being read carefully is the whole point. Small, and
 * exactly the kind of thing that makes a document look translated rather
 * than written.
 */
const LAST_UPDATED = { en: '25 September 2026', it: '25 settembre 2026' };

/**
 * There was an amber "awaiting legal review" banner above these terms, saying
 * not to open the site to real users until a professional had read them. The
 * owner decided on 24 September 2026 not to commission that review, so it was
 * removed rather than left promising something that is not coming. The
 * decision and what it costs are in LEGAL_REVIEW.md section 7.
 */

export default function Terms() {
  const { lang, t } = useI18n();
  const italian = lang === 'it';

  return (
    <div className="container section" style={{ maxWidth: 780 }}>
      <BackLink to="/" label={italian ? 'Torna a ChoreConnect' : 'Back to ChoreConnect'} />

      <div className="page-header">
        <span className="feature-icon blue">
          <FileText size={26} aria-hidden="true" />
        </span>
        <h1>{italian ? 'Termini di servizio' : 'Terms of service'}</h1>
        <p>
          {italian
            ? `Le regole per usare ${APP_NAME}. Ultimo aggiornamento: ${LAST_UPDATED.it}.`
            : `The rules for using ${APP_NAME}. Last updated ${LAST_UPDATED.en}.`}
        </p>
      </div>

      {italian ? <TermsIt /> : <TermsEn />}
    </div>
  );
}
